import { Scene } from "@babylonjs/core/scene";
import { Vector3, Color3, Color4, Quaternion } from "@babylonjs/core/Maths/math";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";

import type { GameScene } from "../engine/SceneManager";
import type { Game } from "../engine/Game";
import { partById } from "../parts/PartCatalog";
import type { PlacedPart, RocketDesign } from "../parts/Rocket";
import { BODIES, bodyById, bodyPosition, resolveParent } from "../world/SolarSystem";
import { Vessel } from "../flight/Vessel";
import { stepVessel, changeReferenceBody } from "../flight/Physics";
import { stateToOrbit, sampleOrbitPath, type Orbit } from "../world/PatchedConics";
import { h, clear } from "../ui/dom";
import { showToast } from "../ui/Toast";
import { showModal } from "../ui/Modal";
import { audio } from "../ui/Audio";
import { explainFailure, type FailureCause } from "../meta/FailureExplain";
import { TutorialRunner } from "../meta/Tutorial";
import { buildPartMesh } from "../parts/PartMesh";

const WARPS = [1, 5, 10, 50, 100, 1000, 10000, 100000];

/** Rendering scale for large celestial bodies. Physics stays SI. */
const RENDER_SCALE = 1 / 1000;

export class FlightScene implements GameScene {
  readonly babylonScene: Scene;
  private camera!: ArcRotateCamera;
  private vessel!: Vessel;
  private design!: RocketDesign;
  private vesselRoot!: TransformNode;
  private partMeshes = new Map<string, TransformNode>();
  private bodyMeshes = new Map<string, Mesh>();
  private warpIdx = 0;
  private running = true;
  private failed = false;
  private uiEl?: HTMLElement;
  private mapOpen = false;
  private mapEl?: HTMLElement;
  private mapRedrawTimer?: number;
  private flameMeshes = new Map<string, Mesh>();
  private lastPhysicsTime = 0;
  private tutorial?: TutorialRunner;
  private epochSeconds = 0;

  // Steering (joystick state)
  private steeringActive = false;
  private steeringStartX = 0;
  private steeringStartY = 0;
  private steeringThumbEl?: HTMLElement;
  private steeringPadEl?: HTMLElement;

  constructor(readonly game: Game, arg?: unknown) {
    this.babylonScene = new Scene(game.engine);
    this.babylonScene.clearColor = new Color4(0.01, 0.015, 0.03, 1);
    const designId = (arg as { designId?: string } | undefined)?.designId;
    if (!designId) throw new Error("FlightScene needs { designId }");
    (this as any)._pendingDesignId = designId;

    // Camera up-front so the render loop never sees a camera-less scene.
    this.camera = new ArcRotateCamera("cam", -Math.PI / 2, Math.PI / 2.4, 22, Vector3.Zero(), this.babylonScene);
    this.camera.minZ = 0.05;
    this.camera.maxZ = 1e7;
    this.camera.lowerRadiusLimit = 6;
    this.camera.upperRadiusLimit = 5000;
    this.babylonScene.activeCamera = this.camera;
  }

  async enter() {
    const designId = (this as any)._pendingDesignId as string;
    const loaded = await this.game.saveManager.getRocket(designId);
    if (!loaded) throw new Error("Rocket not found");
    this.design = loaded;
    this.vessel = new Vessel(this.design);

    const earth = bodyById("earth");
    this.vessel.parentId = "earth";
    this.vessel.rx = 0;
    // Sit the rocket on the pad: vessel origin at earth.radius + rocketBase
    // means the rocket's lowest part touches ground at spawn.
    this.vessel.ry = earth.radius;
    this.vessel.vx = 0;
    this.vessel.vy = 0;
    this.vessel.heading = 0;

    (window as any).__flt = this;
    this.setupScene();
    this.setupUi();
    this.setupInput();
    audio.startThrust();

    const seen = await this.game.saveManager.kvGet<boolean>("tutorial.flight.seen", false);
    if (!seen) {
      this.tutorial = new TutorialRunner(this.game.uiRoot, [
        { title: "Throttle up", body: "Drag the throttle bar on the left all the way up." },
        { title: "Steering", body: "Use the circular pad at bottom-right to tilt your rocket. Push right to tilt East after a few seconds of straight-up flight." },
        { title: "Stage", body: "Tap STAGE to drop empty tanks, decouple boosters, or deploy parachutes." },
        { title: "Map", body: "Tap 🗺 Map to see your orbit and the solar system. Tap ✕ Close to return." },
      ]);
      this.tutorial.start();
      await this.game.saveManager.kvSet("tutorial.flight.seen", true);
    }

    this.babylonScene.registerBeforeRender(() => this.tick());
    this.lastPhysicsTime = performance.now();

    void this.game.achievements.unlock("first-launch");
  }

  // ------------------ Scene ------------------
  private setupScene() {
    const scene = this.babylonScene;

    // Camera was created in the constructor so the render loop always has one; no attachControl.

    const hemi = new HemisphericLight("hemi", new Vector3(0, 1, 0), scene);
    hemi.intensity = 0.4;
    hemi.groundColor = new Color3(0.05, 0.08, 0.12);
    const sun = new DirectionalLight("sun", new Vector3(-0.4, -0.8, 0.4), scene);
    sun.intensity = 1.2;

    // Starfield — emissive point sprites on a huge sphere so they're always visible.
    const sky = MeshBuilder.CreateSphere("sky", { diameter: 2e5, segments: 16 }, scene);
    sky.infiniteDistance = true;
    const skyMat = new StandardMaterial("skyMat", scene);
    skyMat.backFaceCulling = false;
    skyMat.disableLighting = true;
    skyMat.emissiveColor = Color3.FromHexString("#050713");
    sky.material = skyMat;
    sky.isPickable = false;
    this.makeStarfield(scene);

    // Build (scaled-down) body meshes. Enabled one-at-a-time based on current parent.
    for (const b of BODIES) {
      if (b.id === "sun") continue;
      const r = b.radius * RENDER_SCALE;
      const mesh = MeshBuilder.CreateSphere(`body_${b.id}`, { diameter: r * 2, segments: 48 }, scene);
      const mat = new StandardMaterial(`bodyMat_${b.id}`, scene);
      mat.diffuseColor = Color3.FromHexString(b.color);
      mat.specularColor = new Color3(0.05, 0.05, 0.05);
      mat.emissiveColor = Color3.FromHexString(b.color).scale(0.06);
      mesh.material = mat;
      mesh.setEnabled(false);
      mesh.isPickable = false;
      this.bodyMeshes.set(b.id, mesh);
    }

    // Launch pad beneath the vessel (static — parented to vesselRoot so it stays at origin).
    this.buildLaunchPad(scene);

    // Vessel root + parts (physical scale — a 6m rocket is 6 units)
    this.vesselRoot = new TransformNode("vesselRoot", scene);
    for (const p of this.design.parts) {
      const group = buildPartMesh(scene, partById(p.partId), `flight_${p.uid}`);
      group.parent = this.vesselRoot;
      const def = partById(p.partId);
      group.position.set(p.x, p.y + def.height / 2, 0);
      this.partMeshes.set(p.uid, group);
    }
  }

  /** Sprinkle small emissive spheres on the inside of a large sphere for stars.
   *  They render on top of the sky because we disable their depth write / depth test. */
  private makeStarfield(scene: Scene) {
    const group = new TransformNode("stars", scene);
    const count = 220;
    const radius = 9e4; // just inside the sky sphere
    for (let i = 0; i < count; i++) {
      // Uniform point on sphere (Marsaglia method)
      let x: number, y: number, s = 2;
      while (s >= 1) {
        x = Math.random() * 2 - 1;
        y = Math.random() * 2 - 1;
        s = x * x + y * y;
      }
      const factor = 2 * Math.sqrt(1 - s);
      const px = x! * factor;
      const py = y! * factor;
      const pz = 1 - 2 * s;
      const size = 60 + Math.random() * 180;
      const star = MeshBuilder.CreateBox(`star_${i}`, { size }, scene);
      const m = new StandardMaterial(`starM_${i}`, scene);
      const bright = 0.6 + Math.random() * 0.4;
      m.emissiveColor = new Color3(bright, bright, bright * (0.9 + Math.random() * 0.2));
      m.disableLighting = true;
      star.material = m;
      star.isPickable = false;
      star.parent = group;
      star.position.set(px * radius, py * radius, pz * radius);
    }
  }

  /** A small launch pad + service tower at the rocket's base — strong "you're on the ground" cue. */
  private launchPadGroup?: TransformNode;
  private buildLaunchPad(scene: Scene) {
    const grp = new TransformNode("launchpad", scene);

    // Concrete slab
    const slab = MeshBuilder.CreateCylinder("padSlab", {
      diameterTop: 6,
      diameterBottom: 7,
      height: 0.4,
      tessellation: 32,
    }, scene);
    const slabMat = new StandardMaterial("padSlabM", scene);
    slabMat.diffuseColor = new Color3(0.25, 0.26, 0.28);
    slabMat.specularColor = new Color3(0, 0, 0);
    slab.material = slabMat;
    slab.parent = grp;
    slab.position.y = -0.2;
    slab.isPickable = false;

    // Flame trench (darker hex in the middle)
    const trench = MeshBuilder.CreateCylinder("trench", {
      diameter: 1.8,
      height: 0.1,
      tessellation: 16,
    }, scene);
    const trM = new StandardMaterial("trenchM", scene);
    trM.diffuseColor = new Color3(0.08, 0.08, 0.08);
    trM.specularColor = new Color3(0, 0, 0);
    trench.material = trM;
    trench.position.y = 0.01;
    trench.parent = grp;
    trench.isPickable = false;

    // 4-post launch tower fully surrounding the rocket with lattice on every side.
    // The rocket is visibly "inside" the cage from every camera angle.
    const towerHeight = 9;
    const towerX = 1.5; // distance from rocket centerline — snug but not clipping parts
    const towerZ = 1.2;
    const gantryColor = new Color3(0.55, 0.25, 0.12);
    const gantryMat = new StandardMaterial("gantryMat", scene);
    gantryMat.diffuseColor = gantryColor;
    gantryMat.specularColor = new Color3(0.15, 0.12, 0.1);

    // 4 corner uprights
    for (const sx of [-towerX, towerX]) {
      for (const sz of [-towerZ, towerZ]) {
        const post = MeshBuilder.CreateBox(`pTower_${sx}_${sz}`, { width: 0.18, height: towerHeight, depth: 0.18 }, scene);
        post.material = gantryMat;
        post.position.set(sx, towerHeight / 2, sz);
        post.parent = grp;
        post.isPickable = false;
      }
    }

    // Horizontal rungs on all 4 sides — these are the "ladder" rungs the user wants visible.
    for (let i = 1; i <= 6; i++) {
      const y = i * (towerHeight / 7);
      // X-axis rungs (front and back)
      for (const sz of [-towerZ, towerZ]) {
        const rung = MeshBuilder.CreateBox(`rungX_${i}_${sz}`, {
          width: towerX * 2, height: 0.06, depth: 0.08,
        }, scene);
        rung.material = gantryMat;
        rung.position.set(0, y, sz);
        rung.parent = grp;
        rung.isPickable = false;
      }
      // Z-axis rungs (left and right)
      for (const sx of [-towerX, towerX]) {
        const rung = MeshBuilder.CreateBox(`rungZ_${i}_${sx}`, {
          width: 0.08, height: 0.06, depth: towerZ * 2,
        }, scene);
        rung.material = gantryMat;
        rung.position.set(sx, y, 0);
        rung.parent = grp;
        rung.isPickable = false;
      }
    }

    // Diagonal braces on the front & back faces for structural feel
    for (const sz of [-towerZ, towerZ]) {
      const d1 = MeshBuilder.CreateBox(`diagA_${sz}`, { width: 0.06, height: 0.06, depth: Math.hypot(towerX * 2, towerHeight) }, scene);
      d1.material = gantryMat;
      d1.position.set(0, towerHeight / 2, sz);
      d1.rotation.y = Math.PI / 2;
      d1.rotation.x = Math.atan2(towerHeight, towerX * 2);
      d1.parent = grp;
      d1.isPickable = false;
    }

    // Top crown: a frame of 4 bars (not a solid roof so the rocket is visible from above)
    const topY = towerHeight;
    for (const sz of [-towerZ, towerZ]) {
      const bar = MeshBuilder.CreateBox(`topX_${sz}`, { width: towerX * 2, height: 0.12, depth: 0.12 }, scene);
      bar.material = gantryMat;
      bar.position.set(0, topY, sz);
      bar.parent = grp;
      bar.isPickable = false;
    }
    for (const sx of [-towerX, towerX]) {
      const bar = MeshBuilder.CreateBox(`topZ_${sx}`, { width: 0.12, height: 0.12, depth: towerZ * 2 }, scene);
      bar.material = gantryMat;
      bar.position.set(sx, topY, 0);
      bar.parent = grp;
      bar.isPickable = false;
    }

    // Horizon ground plane — a large dark disc extending outward to read as "ground"
    const ground = MeshBuilder.CreateDisc("groundRing", { radius: 400, tessellation: 48 }, scene);
    const gMat = new StandardMaterial("groundRingM", scene);
    gMat.diffuseColor = new Color3(0.18, 0.21, 0.25);
    gMat.specularColor = new Color3(0, 0, 0);
    gMat.emissiveColor = new Color3(0.02, 0.03, 0.04);
    ground.material = gMat;
    ground.rotation.x = Math.PI / 2;
    ground.position.y = -0.45;
    ground.parent = grp;
    ground.isPickable = false;

    this.launchPadGroup = grp;
  }

  private ensureFlameMesh(uid: string) {
    if (this.flameMeshes.has(uid)) return this.flameMeshes.get(uid)!;
    const part = this.design.parts.find((p) => p.uid === uid);
    if (!part) throw new Error("flame for unknown part");
    const def = partById(part.partId);

    // Plume: wide at the nozzle exit, tapering to a point downward.
    // Cylinder with diameterTop = wide (becomes the nozzle-exit end), diameterBottom = 0 (tail).
    const flameHeight = def.height * 2.2;
    const flame = MeshBuilder.CreateCylinder(`flame_${uid}`, {
      diameterTop: def.halfWidth * 1.7,
      diameterBottom: 0,
      height: flameHeight,
      tessellation: 16,
    }, this.babylonScene);

    const mat = new StandardMaterial(`flameMat_${uid}`, this.babylonScene);
    mat.emissiveColor = new Color3(1, 0.6, 0.2);
    mat.disableLighting = true;
    mat.alpha = 0.85;
    flame.material = mat;
    flame.isPickable = false;
    flame.parent = this.vesselRoot;

    // Nozzle exit in the vessel's local frame = engine's bottom y (= part.y, since local y=0
    // in the engine mesh is the bottom attach plane in its builder layout).
    const nozzleY = part.y;
    // Flame mesh's top face is at y = position.y + height/2. Set top to nozzle exit.
    flame.position.set(part.x, nozzleY - flameHeight / 2, 0);

    this.flameMeshes.set(uid, flame);
    return flame;
  }

  // ------------------ UI ------------------
  private setupUi() {
    const root = this.game.uiRoot;
    clear(root);

    const hud = h("div", { class: "flight-hud" }, []);

    const top = h("div", { class: "hud-top" }, [
      h("div", { class: "hud-stats" }, [
        stat("Altitude", "0 m", "flt-alt"),
        stat("Speed", "0 m/s", "flt-spd"),
        stat("Apoapsis", "—", "flt-apo"),
        stat("Periapsis", "—", "flt-per"),
      ]),
      h("div", { style: "display:flex; gap:8px;" }, [
        h("button", { class: "btn", id: "flt-mute", onclick: () => this.toggleMute() }, [audio.isMuted() ? "🔇" : "🔊"]),
        h("button", { class: "btn", onclick: () => this.revert() }, ["↺ Revert"]),
        h("button", { class: "btn primary", onclick: () => this.toggleMap() }, ["🗺 Map"]),
      ]),
    ]);

    const throttle = h("div", { class: "throttle", id: "flt-throttle" }, [
      h("div", { class: "throttle-fill", id: "flt-throttle-fill" }, []),
      h("div", { class: "throttle-label" }, ["THR"]),
    ]);
    throttle.addEventListener("pointerdown", (e) => this.onThrottlePointer(e));

    const warp = h("div", { class: "time-warp panel", id: "flt-warp" }, []);
    this.renderWarpButtons(warp);

    const stageBtn = h("button", { class: "btn primary stage-btn", onclick: () => this.doStage() }, ["STAGE"]);

    // Steering joystick (right-bottom, above stage)
    const joystick = this.buildJoystick();

    // Camera zoom + view buttons (right-top)
    const right = h("div", { class: "flight-right" }, [
      h("button", { class: "btn", onclick: () => this.zoomCamera(-1) }, ["➕"]),
      h("button", { class: "btn", onclick: () => this.zoomCamera(+1) }, ["➖"]),
      h("button", { class: "btn", onclick: () => this.killSteer() }, ["⇑"]),
      h("button", { class: "btn", onclick: () => this.deployChutes() }, ["🪂"]),
    ]);

    hud.appendChild(top);
    hud.appendChild(throttle);
    hud.appendChild(warp);
    hud.appendChild(joystick);
    hud.appendChild(stageBtn);
    hud.appendChild(right);

    root.appendChild(hud);
    this.uiEl = hud;

    function stat(label: string, value: string, id: string) {
      return h("div", { class: "hud-stat panel" }, [
        h("div", { class: "label" }, [label]),
        h("div", { class: "value", id }, [value]),
      ]);
    }
  }

  private buildJoystick(): HTMLElement {
    const pad = h("div", { class: "joystick", id: "flt-joy" }, [
      h("div", { class: "joystick-thumb", id: "flt-joy-thumb" }, []),
      h("div", { class: "joystick-label" }, ["STEER"]),
    ]);
    pad.addEventListener("pointerdown", (e) => this.onJoyDown(e));
    this.steeringPadEl = pad;
    this.steeringThumbEl = pad.querySelector("#flt-joy-thumb") as HTMLElement;
    return pad;
  }

  private renderWarpButtons(host: HTMLElement) {
    clear(host);
    for (let i = 0; i < WARPS.length; i++) {
      const w = WARPS[i];
      host.appendChild(
        h(
          "button",
          { class: `btn ${i === this.warpIdx ? "current" : ""}`, onclick: () => this.setWarp(i) },
          [w >= 1000 ? `${w / 1000}k` : String(w)],
        ),
      );
    }
  }

  private setWarp(idx: number) {
    this.warpIdx = Math.max(0, Math.min(WARPS.length - 1, idx));
    const host = document.getElementById("flt-warp");
    if (host) this.renderWarpButtons(host);
    audio.play("tap");
  }

  // ------------------ Input ------------------
  private setupInput() {
    window.addEventListener("keydown", this.onKeyDown);
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "Space") { e.preventDefault(); this.doStage(); }
    else if (e.code === "KeyX") this.vessel.throttle = 0;
    else if (e.code === "KeyZ") this.vessel.throttle = 1;
    else if (e.code === "ArrowLeft") this.vessel.heading -= 0.05;
    else if (e.code === "ArrowRight") this.vessel.heading += 0.05;
    else if (e.code === "ArrowUp") this.vessel.heading = 0;
    else if (e.code === "Period") this.setWarp(this.warpIdx + 1);
    else if (e.code === "Comma") this.setWarp(this.warpIdx - 1);
    else if (e.code === "KeyM") this.toggleMap();
    else if (e.code === "Escape") { if (this.mapOpen) this.closeMap(); }
  };

  private onJoyDown(e: PointerEvent) {
    e.preventDefault();
    this.steeringActive = true;
    const rect = (this.steeringPadEl as HTMLElement).getBoundingClientRect();
    this.steeringStartX = rect.left + rect.width / 2;
    this.steeringStartY = rect.top + rect.height / 2;
    const move = (ev: PointerEvent) => this.onJoyMove(ev);
    const up = () => {
      this.steeringActive = false;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      if (this.steeringThumbEl) this.steeringThumbEl.style.transform = "translate(-50%, -50%)";
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    this.onJoyMove(e);
  }

  private onJoyMove(e: PointerEvent) {
    if (!this.steeringActive || !this.steeringPadEl) return;
    const rect = this.steeringPadEl.getBoundingClientRect();
    const half = rect.width / 2;
    const dx = Math.max(-half, Math.min(half, e.clientX - this.steeringStartX));
    const dy = Math.max(-half, Math.min(half, e.clientY - this.steeringStartY));
    if (this.steeringThumbEl) {
      this.steeringThumbEl.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    }
    // Map X to heading rate, Y to immediate direct pitch adjustment
    const xNorm = dx / half; // -1..+1
    // Apply as heading rate (deg/s at full deflection = 90).
    const ratePerFrame = xNorm * 0.025; // ~1.4°/frame at 60fps = ~85°/s
    this.vessel.heading += ratePerFrame;
    // Clamp to -π..π
    if (this.vessel.heading > Math.PI) this.vessel.heading -= 2 * Math.PI;
    if (this.vessel.heading < -Math.PI) this.vessel.heading += 2 * Math.PI;
  }

  private killSteer() {
    this.vessel.heading = 0;
    audio.play("tap");
  }

  private toggleMute() {
    audio.setMuted(!audio.isMuted());
    const btn = document.getElementById("flt-mute");
    if (btn) btn.textContent = audio.isMuted() ? "🔇" : "🔊";
  }

  private zoomCamera(direction: number) {
    const factor = direction > 0 ? 1.4 : 0.7;
    this.camera.radius = Math.max(
      this.camera.lowerRadiusLimit!,
      Math.min(this.camera.upperRadiusLimit!, this.camera.radius * factor),
    );
  }

  private onThrottlePointer(e: PointerEvent) {
    e.preventDefault();
    const el = document.getElementById("flt-throttle");
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const apply = (clientY: number) => {
      const local = clientY - rect.top;
      const h01 = 1 - Math.max(0, Math.min(1, local / rect.height));
      this.vessel.throttle = h01;
      const fill = document.getElementById("flt-throttle-fill");
      if (fill) fill.style.height = `${h01 * 100}%`;
    };
    apply(e.clientY);
    const move = (ev: PointerEvent) => apply(ev.clientY);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  // ------------------ Staging ------------------
  private doStage() {
    audio.play("thud");
    const decoupler = this.vessel.attachedParts
      .map((p) => ({ p, d: partById(p.partId) }))
      .find(({ d, p }) => d.isDecoupler && p.stage <= this.vessel.activeStage + 1);

    if (decoupler) {
      const cutoffY = decoupler.p.y;
      const toDrop = this.vessel.attachedParts.filter((p) => p.y <= cutoffY && p.uid !== decoupler.p.uid);
      toDrop.push(decoupler.p);
      for (const p of toDrop) {
        this.vessel.attachedUids.delete(p.uid);
        const m = this.partMeshes.get(p.uid);
        if (m) { m.dispose(); this.partMeshes.delete(p.uid); }
        const f = this.flameMeshes.get(p.uid);
        if (f) { f.dispose(); this.flameMeshes.delete(p.uid); }
        this.vessel.fuel.solidByUid.delete(p.uid);
      }
      this.vessel.recomputeMass();
      showToast(this.game.uiRoot, `Dropped ${toDrop.length} parts`, "💥", 1600);
    }

    let chuteDeployed = false;
    for (const p of this.vessel.attachedParts) {
      const d = partById(p.partId);
      if (d.chuteDragMult && !this.vessel.parachuteDeployed && p.stage <= this.vessel.activeStage + 1) {
        this.vessel.parachuteDeployed = true;
        chuteDeployed = true;
      }
    }
    if (chuteDeployed) {
      showToast(this.game.uiRoot, "Parachute deployed", "🪂", 1600);
      audio.play("whoosh");
    }

    this.vessel.activeStage++;
  }

  private deployChutes() {
    const hasChute = this.vessel.attachedParts.some((p) => partById(p.partId).chuteDragMult);
    if (!hasChute) {
      showToast(this.game.uiRoot, "No parachute on this rocket", "⚠️", 2200);
      audio.play("error");
      return;
    }
    this.vessel.parachuteDeployed = true;
    showToast(this.game.uiRoot, "Parachute deployed", "🪂", 1600);
    audio.play("whoosh");
  }

  // ------------------ Main tick ------------------
  private tick() {
    if (!this.running) return;
    const now = performance.now();
    const realDt = Math.min(0.05, (now - this.lastPhysicsTime) / 1000);
    this.lastPhysicsTime = now;

    const warp = WARPS[this.warpIdx];
    const simDt = realDt * warp;
    this.epochSeconds += simDt;

    if (!this.failed && !this.vessel.destroyed) {
      const maxStep = 0.1;
      const steps = Math.max(1, Math.min(120, Math.ceil(simDt / maxStep)));
      const dtStep = simDt / steps;
      for (let i = 0; i < steps; i++) {
        stepVessel(this.vessel, dtStep);
        if (this.vessel.destroyed) break;
        this.checkSoiTransitions();
      }
    }

    // Audio thrust modulation
    const engines = this.vessel.activeEngines();
    const thrustIntensity = engines.length > 0 ? this.vessel.throttle : 0;
    audio.updateThrust(thrustIntensity);

    this.updateRenderTransforms();
    this.updateHud();

    if (this.vessel.destroyed && !this.failed) {
      this.failed = true;
      audio.play("thud");
      this.showFailureModal();
    }
  }

  private checkSoiTransitions() {
    const newParent = resolveParent(this.vessel.parentId, this.vessel.rx, this.vessel.ry, this.epochSeconds);
    if (newParent !== this.vessel.parentId) {
      changeReferenceBody(this.vessel, newParent, this.epochSeconds);
      const b = bodyById(newParent);
      showToast(this.game.uiRoot, `Entered ${b.name}'s sphere of influence`, "🛰", 2800);
      audio.play("chime");
      const key = `${newParent}-flyby`;
      // Map our bodies to achievement ids
      const map: Record<string, string> = {
        moon: "moon-flyby",
        mars: "mars-flyby",
        venus: "venus-flyby",
        mercury: "mercury-flyby",
      };
      if (map[newParent]) void this.game.achievements.unlock(map[newParent]);
    }
  }

  // ------------------ Rendering ------------------
  private updateRenderTransforms() {
    const parent = bodyById(this.vessel.parentId);
    const vesselMesh = this.vesselRoot;

    // Rendering strategy: rocket parts render at true physical scale, so engine is 0.9m,
    // capsule is 1.2m etc. — exactly as they look in the builder. The parent body is
    // *scaled* (RENDER_SCALE) to keep float precision manageable, but its scene position
    // is set so that the visible surface sits at scene_y = (altitude - rocketBase) below
    // the vessel root at any altitude. At touchdown the engine nozzle sits on ground.
    const altitude = this.vessel.altitude(parent.radius); // real metres above surface
    const rocketBase = this.lowestAttachedPartY();        // local-y of lowest attached part

    const bodyMesh = this.bodyMeshes.get(this.vessel.parentId);
    const surfaceY = rocketBase - altitude; // scene-y of the ground directly under the vessel
    if (bodyMesh) {
      for (const [id, m] of this.bodyMeshes) m.setEnabled(id === this.vessel.parentId);
      // We want the sphere's "top point" to appear at scene_y = surfaceY.
      // Top point = center + body.radius*RENDER_SCALE.
      bodyMesh.position.set(0, surfaceY - parent.radius * RENDER_SCALE, 0);
    }

    // Launch pad sits on the ground directly under the rocket — follow the lowest
    // attached part's x so the pad stays aligned with the engine even if the user
    // built an off-center rocket.
    if (this.launchPadGroup) {
      const nearGround = altitude < 2000; // meters
      this.launchPadGroup.setEnabled(nearGround && this.vessel.parentId === "earth");
      this.launchPadGroup.position.set(this.lowestAttachedPartX(), surfaceY, 0);
    }

    // Rotate the rocket around its own base, not the scene origin. Without this,
    // tilting the rocket via steering sweeps the base away from the stationary
    // launch pad — base and pad only appear aligned at heading = 0.
    this.vesselRoot.setPivotPoint(new Vector3(this.lowestAttachedPartX(), rocketBase, 0));
    vesselMesh.position.set(0, 0, 0);
    vesselMesh.rotationQuaternion = Quaternion.RotationAxis(new Vector3(0, 0, 1), -this.vessel.heading);

    // Camera targets the middle of the vessel so longer rockets still fit the view.
    const vesselMid = this.vesselMiddleY();
    this.camera.target = new Vector3(0, vesselMid, 0);
    // Keep the camera alpha roughly "behind" the rocket so nose points up in view.
    this.camera.alpha = -Math.PI / 2 + this.vessel.heading;

    // Engine flames
    const activeEngineUids = new Set(this.vessel.activeEngines().map((e) => e.uid));
    for (const [uid, mesh] of this.flameMeshes) {
      if (!activeEngineUids.has(uid)) { mesh.dispose(); this.flameMeshes.delete(uid); }
    }
    for (const uid of activeEngineUids) {
      const mesh = this.ensureFlameMesh(uid);
      const def = partById(this.vessel.attachedParts.find((p) => p.uid === uid)!.partId);
      const mat = mesh.material as StandardMaterial;
      const throttle = def.throttleable ? this.vessel.throttle : 1;
      mesh.scaling.y = Math.max(0.2, throttle * (1 + Math.random() * 0.1));
      mat.alpha = 0.6 + 0.3 * Math.random();
    }
  }

  /** Lowest local-Y of any attached part (where the rocket "sits" on ground). */
  private lowestAttachedPartY(): number {
    let min = Number.POSITIVE_INFINITY;
    for (const p of this.vessel.attachedParts) {
      if (p.y < min) min = p.y;
    }
    return min === Number.POSITIVE_INFINITY ? 0 : min;
  }

  /** The x-position of the bottom-most part (where the rocket's feet are). */
  private lowestAttachedPartX(): number {
    let minY = Number.POSITIVE_INFINITY;
    let x = 0;
    for (const p of this.vessel.attachedParts) {
      if (p.y < minY) { minY = p.y; x = p.x; }
    }
    return x;
  }

  /** Midpoint local-Y of attached parts (for camera framing). */
  private vesselMiddleY(): number {
    if (this.vessel.attachedParts.length === 0) return 0;
    let lo = Number.POSITIVE_INFINITY;
    let hi = Number.NEGATIVE_INFINITY;
    for (const p of this.vessel.attachedParts) {
      const d = partById(p.partId);
      lo = Math.min(lo, p.y);
      hi = Math.max(hi, p.y + d.height);
    }
    return (lo + hi) * 0.5;
  }

  private updateHud() {
    const body = bodyById(this.vessel.parentId);
    const alt = this.vessel.altitude(body.radius);
    const spd = Math.hypot(this.vessel.vx, this.vessel.vy);

    setText("flt-alt", formatDistance(alt));
    setText("flt-spd", `${spd.toFixed(0)} m/s`);

    if (spd > 1) {
      const orbit = stateToOrbit(this.vessel.parentId, this.vessel.rx, this.vessel.ry, this.vessel.vx, this.vessel.vy, this.epochSeconds);
      setText("flt-apo", orbit.apoapsis === Infinity ? "escape" : formatDistance(orbit.apoapsis - body.radius));
      setText("flt-per", formatDistance(orbit.periapsis - body.radius));

      if (orbit.periapsis > body.radius + body.atmosphereHeight && this.vessel.parentId === "earth") {
        void this.game.achievements.unlock("orbit-earth");
      }
    }

    if (this.vessel.parentId === "moon" && this.vessel.landed) {
      void this.game.achievements.unlock("moon-landing");
    }

    const fill = document.getElementById("flt-throttle-fill");
    if (fill) fill.style.height = `${this.vessel.throttle * 100}%`;
  }

  // ------------------ Map view ------------------
  private toggleMap() {
    if (this.mapOpen) this.closeMap();
    else this.openMap();
  }

  private openMap() {
    this.mapOpen = true;
    audio.play("tap");
    const root = this.game.uiRoot;

    const layer = h("div", { class: "map-ui" }, []);

    // Big always-visible close button, top-left (kid-friendly)
    const closeBar = h("div", { class: "map-close-bar" }, [
      h("button", { class: "btn primary", onclick: () => this.closeMap() }, ["← Back to Flight"]),
      h("div", { class: "muted", style: "font-size:12px" }, ["Tap anywhere outside the list, or press ESC"]),
    ]);
    layer.appendChild(closeBar);

    const bodies = h("div", { class: "map-bodies panel" }, [h("h3", {}, ["Destinations"])]);
    for (const b of BODIES) {
      if (b.id === "sun") continue;
      const row = h("div", { class: "map-body-row" }, [
        h("div", { class: "dot", style: `background:${b.color}` }, []),
        h("div", {}, [b.name]),
      ]);
      bodies.appendChild(row);
    }
    layer.appendChild(bodies);

    const canvas = document.createElement("canvas");
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    canvas.style.cssText = "position:absolute;inset:0;pointer-events:auto;z-index:0;";
    // Click anywhere on empty map area to close — but only if target is the canvas itself
    canvas.addEventListener("click", (e) => {
      if (e.target === canvas) this.closeMap();
    });
    layer.appendChild(canvas);
    // Make sure canvas is underneath the buttons (which we add later)
    layer.insertBefore(canvas, closeBar);

    root.appendChild(layer);
    this.mapEl = layer;

    const redraw = () => this.drawMap(canvas);
    redraw();
    this.mapRedrawTimer = window.setInterval(redraw, 500);

    const onResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      redraw();
    };
    window.addEventListener("resize", onResize);
    (this as any)._mapResize = onResize;
  }

  private closeMap() {
    if (!this.mapOpen) return;
    this.mapOpen = false;
    audio.play("tap");
    this.mapEl?.remove();
    this.mapEl = undefined;
    if (this.mapRedrawTimer) { clearInterval(this.mapRedrawTimer); this.mapRedrawTimer = undefined; }
    const onResize = (this as any)._mapResize as (() => void) | undefined;
    if (onResize) window.removeEventListener("resize", onResize);
  }

  private drawMap(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d")!;
    const w = canvas.width, hgt = canvas.height;
    ctx.clearRect(0, 0, w, hgt);
    ctx.fillStyle = "#05070d";
    ctx.fillRect(0, 0, w, hgt);

    const cx = w / 2;
    const cy = hgt / 2;

    const parent = bodyById(this.vessel.parentId);
    const spd = Math.hypot(this.vessel.vx, this.vessel.vy);
    let orbit: Orbit | null = null;
    if (spd > 1) {
      orbit = stateToOrbit(this.vessel.parentId, this.vessel.rx, this.vessel.ry, this.vessel.vx, this.vessel.vy, this.epochSeconds);
    }

    const maxRadius = Math.max(
      parent.soi === Infinity ? parent.radius * 10 : parent.soi,
      orbit?.apoapsis === Infinity ? parent.radius * 10 : orbit?.apoapsis ?? parent.radius * 2,
      Math.hypot(this.vessel.rx, this.vessel.ry),
    );
    const viewRadius = maxRadius * 1.2;
    const scale = Math.min(w, hgt) / 2 / viewRadius;

    // Parent body
    ctx.fillStyle = parent.color;
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(2, parent.radius * scale), 0, Math.PI * 2);
    ctx.fill();

    if (parent.atmosphereHeight > 0) {
      ctx.strokeStyle = "rgba(74,168,255,0.3)";
      ctx.beginPath();
      ctx.arc(cx, cy, (parent.radius + parent.atmosphereHeight) * scale, 0, Math.PI * 2);
      ctx.stroke();
    }

    if (parent.soi !== Infinity) {
      ctx.strokeStyle = "rgba(255,255,255,0.12)";
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.arc(cx, cy, parent.soi * scale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    for (const b of BODIES) {
      if (b.parent !== parent.id) continue;
      const pos = bodyPosition(b, this.epochSeconds);
      const bx = cx + pos.x * scale;
      const by = cy - pos.y * scale;

      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.beginPath();
      ctx.arc(cx, cy, (b.orbitRadius ?? 0) * scale, 0, Math.PI * 2);
      ctx.stroke();

      ctx.fillStyle = b.color;
      ctx.beginPath();
      ctx.arc(bx, by, Math.max(3, b.radius * scale), 0, Math.PI * 2);
      ctx.fill();

      // Label
      ctx.fillStyle = "#e6ecf5";
      ctx.font = "13px -apple-system, system-ui";
      ctx.fillText(b.name, bx + 8, by - 8);

      // SOI of child
      ctx.strokeStyle = "rgba(255,255,255,0.08)";
      ctx.setLineDash([3, 6]);
      ctx.beginPath();
      ctx.arc(bx, by, b.soi * scale, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    if (orbit && orbit.e < 1) {
      const path = sampleOrbitPath(orbit, 180);
      ctx.strokeStyle = "#4aa8ff";
      ctx.lineWidth = 1.8;
      ctx.beginPath();
      for (let i = 0; i < path.length; i++) {
        const [x, y] = path[i];
        const px = cx + x * scale;
        const py = cy - y * scale;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.stroke();
      ctx.lineWidth = 1;
    }

    const vx = cx + this.vessel.rx * scale;
    const vy = cy - this.vessel.ry * scale;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(vx, vy, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.beginPath();
    ctx.arc(vx, vy, 10, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = "#e6ecf5";
    ctx.font = "14px -apple-system, system-ui";
    ctx.textAlign = "right";
    ctx.fillText(`Parent: ${parent.name}`, w - 16, hgt - 52);
    if (orbit) {
      ctx.fillText(`Apo: ${formatDistance(orbit.apoapsis - parent.radius)}`, w - 16, hgt - 34);
      ctx.fillText(`Peri: ${formatDistance(orbit.periapsis - parent.radius)}`, w - 16, hgt - 16);
    }
    ctx.textAlign = "left";
  }

  // ------------------ Failure / revert ------------------
  private showFailureModal() {
    const cause = this.classifyFailure();
    const info = explainFailure(cause);
    showModal(
      this.game.uiRoot,
      info.title,
      h("div", {}, [
        h("p", {}, [info.message]),
        h("p", { style: "color:var(--text-dim)" }, [`Hint: ${info.hint}`]),
      ]),
      [
        { label: "Menu", onClick: () => void this.game.sceneManager.go("menu") },
        { label: "Revert to Hangar", primary: true, onClick: () => this.revert() },
      ],
    );
  }

  private classifyFailure(): FailureCause {
    if (!this.vessel.hasControl) return "no-control";
    if (this.vessel.peakTempK > 2100) return "too-hot";
    const body = bodyById(this.vessel.parentId);
    const alt = this.vessel.altitude(body.radius);
    if (alt < 50 && Math.hypot(this.vessel.vx, this.vessel.vy) > 10) {
      const hasChute = this.vessel.attachedParts.some((p) => partById(p.partId).chuteDragMult);
      return hasChute ? "crashed" : "no-parachute";
    }
    if (this.vessel.fuel.liquid < 1 && this.vessel.fuel.solidByUid.size === 0) return "out-of-fuel";
    return "crashed";
  }

  private async revert() {
    await this.game.sceneManager.go("builder", { design: this.design });
  }

  // ------------------ Lifecycle ------------------
  leave() {
    this.running = false;
    audio.stopThrust();
    window.removeEventListener("keydown", this.onKeyDown);
  }

  dispose() {
    this.tutorial?.dispose();
    this.babylonScene.dispose();
    this.uiEl?.remove();
    this.mapEl?.remove();
  }
}

function setText(id: string, t: string) {
  const el = document.getElementById(id);
  if (el) el.textContent = t;
}

function formatDistance(m: number): string {
  if (!isFinite(m)) return "∞";
  const abs = Math.abs(m);
  if (abs > 1e9) return `${(m / 1e9).toFixed(2)} Gm`;
  if (abs > 1e6) return `${(m / 1e6).toFixed(2)} Mm`;
  if (abs > 1e3) return `${(m / 1e3).toFixed(1)} km`;
  return `${m.toFixed(0)} m`;
}
